import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Select,
  Badge,
  Row,
  Col,
  message,
  Tooltip,
  Dropdown,
  Drawer,
  Form,
  Input,
  Modal,
  Tag,
} from "antd";
import {
  UserOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  MoreOutlined,
  FilterOutlined,
  SearchOutlined,
  PhoneOutlined,
  IdcardOutlined,
  MailOutlined,
  FileProtectOutlined,
  CreditCardOutlined,
  TransactionOutlined,
} from "@ant-design/icons";
import { formatDate } from "../../utils/format";
import UserProfileDrawer from "./UserProfileDrawer";
import ComplianceDrawer from "./ComplianceDrawer";
import { api } from "../../services/authService";
import UserTransactionsModal from "../transactions/UserTransactionsModal";
import UserStatusBadge from "./UserStatusBadge";

const { Text, Title } = Typography;
const { Option } = Select;

const IndividualUsers = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [complianceDrawerVisible, setComplianceDrawerVisible] = useState(false);
  const [total, setTotal] = useState(0);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1200);
  const [transactionsModalVisible, setTransactionsModalVisible] =
    useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [filterVisible, setFilterVisible] = useState(false);
  const [form] = Form.useForm();
  // const [cardsModalVisible, setCardsModalVisible] = useState(false);
  // const [selectedUserCards, setSelectedUserCards] = useState([]);
  // const [loadingCards, setLoadingCards] = useState(false);

  const observerTarget = useRef(null);
  const LIMIT = 15;
  const filtersRef = useRef(filters);
  const firstMount = useRef(true);

  // Check if there are any active filters
  const hasActiveFilters = useMemo(() => {
    const activeFilterCount = Object.keys(filters).filter(
      (key) =>
        filters[key] !== undefined &&
        filters[key] !== null &&
        filters[key] !== "" &&
        !(Array.isArray(filters[key]) && filters[key].length === 0)
    ).length;
    return activeFilterCount > 0;
  }, [filters]);

  const fetchUsers = useCallback(
    async (reset = false) => {
      if (loading) return;
      if (!reset && !hasMore) return;

      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.set("limit", LIMIT.toString());

        if (!reset && nextCursor) {
          params.set("next_cursor", nextCursor);
        }

        const currentFilters = filtersRef.current;

        // Add search parameters if they exist
        if (currentFilters.id && currentFilters.id.trim()) {
          params.set("user_id", currentFilters.id.trim());
        }
        if (currentFilters.email && currentFilters.email.trim()) {
          params.set("email", currentFilters.email.trim());
        }

        // Add filter parameters if they exist
        if (currentFilters.status) {
          params.set("status", currentFilters.status);
        }
        if (currentFilters.type) {
          params.set("type", currentFilters.type);
        }

        // If any search parameter is present, don't use cursor-based pagination
        const isSearching = !!(
          currentFilters.id ||
          currentFilters.email
        );

        // Remove next_cursor if we're searching
        if (isSearching && params.has("next_cursor")) {
          params.delete("next_cursor");
        }

        const response = await api.get(
          `/admin/individuals?${params.toString()}`
        );
        const data = response.data;

        // Handle single user response (for phone number search)
        if (data.id) {
          setUsers([data]);
          setNextCursor(null);
          setHasMore(false);
          setTotal(1);
          return;
        }

        // Handle regular response with users array
        const newUsers = data.users || [];

        // For user ID search, only show the matching user
        if (currentFilters.id) {
          const matchingUser = newUsers.find(
            (user) => user.id === currentFilters.id
          );
          setUsers(matchingUser ? [matchingUser] : []);
          setNextCursor(null);
          setHasMore(false);
          setTotal(matchingUser ? 1 : 0);
          return;
        }

        // For regular list view
        setUsers(reset ? newUsers : [...users, ...newUsers]);
        setNextCursor(data.next_cursor);
        setHasMore(!!data.next_cursor);
        setTotal(
          data.total !== undefined
            ? data.total
            : reset
            ? newUsers.length
            : users.length + newUsers.length
        );
      } catch (error) {
        console.error("Error fetching individual users:", error);
        message.error("Failed to fetch individual users. Please try again.");
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [nextCursor, loading, hasMore, users]
  );

  // Handle filter submission
  const handleFilterSubmit = useCallback(
    (values) => {
      // Clean up the values by removing empty/undefined values
      const cleanValues = Object.entries(values).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== "" && value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {});

      // Update both the state and ref simultaneously
      setFilters(cleanValues);
      filtersRef.current = cleanValues;

      setFilterVisible(false);
      // Reset pagination when applying new filters
      setNextCursor(null);
      setHasMore(true);
      fetchUsers(true);
    },
    [fetchUsers]
  );

  // Reset filters
  const handleResetFilters = useCallback(() => {
    // Just reset form fields visually without any side effects
    form.resetFields();

    // Use a more direct approach to clear all fields
    const emptyValues = {
      id: undefined,
      email: undefined,
      status: undefined,
      type: undefined,
    };

    form.setFieldsValue(emptyValues);

    // Don't call onFilter, don't close drawer, don't trigger API calls
  }, [form]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    // Reset all state values
    setUsers([]); // Clear existing users
    setNextCursor(null); // Reset pagination cursor
    setHasMore(true); // Reset hasMore flag
    setFilters({}); // Clear filters
    filtersRef.current = {}; // Clear filters ref

    // Clear form fields
    form.resetFields();
    form.setFieldsValue({
      id: undefined,
      email: undefined,
      status: undefined,
      type: undefined,
    });

    // Fetch users with reset flag
    fetchUsers(true);
  }, [fetchUsers, form]);

  // Update the ref whenever filters change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Initial load
  useEffect(() => {
    // Only fetch on first mount
    if (firstMount.current) {
      firstMount.current = false;
      fetchUsers(true);
    }
  }, []); // Only run on mount

  useEffect(() => {
    if (!observerTarget.current || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchUsers(false);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(observerTarget.current);

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
      observer.disconnect();
    };
  }, [hasMore, loading]);

  // Check window size on resize
  useEffect(() => {
    const checkWindowSize = () => {
      setIsSmallScreen(window.innerWidth < 1200);
    };

    window.addEventListener("resize", checkWindowSize);
    return () => window.removeEventListener("resize", checkWindowSize);
  }, []);

  // Helper function to extract email from user data
  const extractEmail = (user) => {
    if (!user) return "N/A";

    return (
      user.email ||
      (user.contact_info && user.contact_info.email) ||
      (user.user_info && user.user_info.email) ||
      (user.profile && user.profile.email) ||
      "N/A"
    );
  };

  // Helper function to extract phone from user data
  const extractPhone = (user) => {
    if (!user) return "N/A";

    return (
      user.phone_number ||
      (user.contact_info &&
        (user.contact_info.phone_number || user.contact_info.phone)) ||
      (user.user_info &&
        (user.user_info.phone_number || user.user_info.phone)) ||
      (user.profile && (user.profile.phone_number || user.profile.phone)) ||
      "N/A"
    );
  };

  const handleUserClick = async (user) => {
    try {
      const response = await api.get(`/admin/individuals/${user.id}`);
      const userData = response.data;

      console.log("DEBUG - Raw user data from API:", userData);

      setSelectedUser({
        ...userData, // Include all properties from the API response
        id: userData.id,
        firstname: userData.firstname,
        lastname: userData.lastname,
        name: `${userData.firstname || ""} ${userData.lastname || ""}`.trim(),
        email: extractEmail(userData),
        phone_number: extractPhone(userData),
        status: userData.status || "N/A",
        is_activated: userData.is_activated === true,
        type: userData.type || "N/A",
        country: userData.country || "N/A",
        created: userData.created,
      });

      setDrawerVisible(true);
    } catch (error) {
      console.error("Error loading individual details:", error);
      message.error("Failed to load individual details. Please try again.");

      // Fallback to displaying the data we already have if API fails
      if (user) {
        console.log("DEBUG - Using fallback with data:", user);

        setSelectedUser({
          ...user, // Include all properties from the list item
          id: user.id,
          firstname: user.firstname,
          lastname: user.lastname,
          name: `${user.firstname || ""} ${user.lastname || ""}`.trim(),
          email: extractEmail(user),
          phone_number: extractPhone(user),
          status: user.status || "N/A",
          is_activated: user.is_activated === true,
          type: user.type || "N/A",
          country: user.country || "N/A",
          created: user.created,
        });
        setDrawerVisible(true);
      }
    }
  };

  const handleStatusChange = async (userId, status) => {
    if (currentUser?.role !== "SUPER_ADMIN") {
      message.error("Only SUPER_ADMIN users can modify user status");
      return;
    }

    try {
      // Use the correct action and status values according to the API documentation
      await api.patch(`/admin/individuals/${userId}`, {
        action: "modify-status",
        status: status, // Should be one of: pending-review, approved, declined
      });

      message.success(`User status updated to ${status}`);

      // Refresh the user list to reflect the changes
      fetchUsers(true);
    } catch (error) {
      console.error("Error updating user status:", error);
      message.error("Failed to update user status. Please try again.");
    }
  };

  // Handle compliance documents click
  const handleComplianceClick = (user) => {
    setSelectedUser(user);
    setComplianceDrawerVisible(true);
  };

  // Get action menu for individual users
  const getActionMenu = (record) => {
    const items = [
      {
        key: "view",
        label: "View Profile",
        icon: <EyeOutlined />,
        onClick: () => handleUserClick(record),
      },
      // {
      //   key: "cards",
      //   label: "View Cards",
      //   icon: <CreditCardOutlined />,
      //   onClick: () => fetchUserCards(record.id),
      // },
      {
        key: "compliance",
        label: "Compliance Documents",
        icon: <FileProtectOutlined />,
        onClick: () => handleComplianceClick(record),
      },
      {
        key: "transactions",
        label: "View Transactions",
        icon: <TransactionOutlined />,
        onClick: () => handleViewTransactions(record),
      },
    ];

    if (
      currentUser?.role === "SUPER_ADMIN" &&
      (record.status === "new" || record.status === "pending-review")
    ) {
      items.push(
        {
          key: "approve",
          icon: <CheckOutlined style={{ color: "#52c41a" }} />,
          label: "Approve User",
          onClick: () => handleStatusChange(record.id, "approved"),
        },
        {
          key: "decline",
          icon: <CloseOutlined style={{ color: "#ff4d4f" }} />,
          label: "Decline User",
          onClick: () => handleStatusChange(record.id, "declined"),
        }
      );
    }

    return items;
  };

  // Handle view transactions
  const handleViewTransactions = (user) => {
    setSelectedUserId(user.id);
    setSelectedUserName(
      `${user.firstname || ""} ${user.lastname || ""}`.trim()
    );
    setTransactionsModalVisible(true);
  };

  // Add function to fetch user cards - Commented out since Cards functionality is hidden
  // const fetchUserCards = async (userId) => {
  //   try {
  //     setLoadingCards(true);
  //     const response = await api.get(`/admin/cards?user_id=${userId}`);
  //     setSelectedUserCards(response.data.cards || []);
  //     setCardsModalVisible(true);
  //   } catch (error) {
  //     console.error("Error fetching user cards:", error);
  //     message.error("Failed to fetch user cards. Please try again.");
  //   } finally {
  //     setLoadingCards(false);
  //   }
  // };

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <Space>
          <UserOutlined />
          {`${record.firstname || ""} ${record.lastname || ""}`}
        </Space>
      ),
    },
    {
      title: "Email",
      key: "email",
      render: (_, record) => extractEmail(record),
    },
    {
      title: "Phone",
      key: "phone",
      render: (_, record) => extractPhone(record),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <UserStatusBadge status={status} />,
    },
    {
      title: "Created",
      key: "created",
      render: (_, record) => {
        // Try different possible field names for created timestamp
        const timestamp =
          record.created || record.created_at || record.timestamp;
        return formatDate(timestamp) || "N/A";
      },
    },
    // {
    //   title: "Cards",
    //   key: "cards",
    //   render: (_, record) => {
    //     const virtualCards = record.virtual_cards?.length || 0;
    //     const physicalCards = record.physical_cards?.length || 0;
    //     return (
    //       <Space direction="vertical" size="small">
    //         <div>
    //           <Tag color="blue">Virtual: {virtualCards}</Tag>
    //         </div>
    //         <div>
    //           <Tag color="green">Physical: {physicalCards}</Tag>
    //         </div>
    //       </Space>
    //     );
    //   },
    // },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 80,
      render: (_, record) => (
        <Dropdown
          menu={{ items: getActionMenu(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              Personal Customers
            </Title>
          </Space>
        }
        extra={
          <Space>
            <Badge dot={hasActiveFilters} offset={[-5, 5]} color="#1890ff">
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilterVisible(true)}
                type={hasActiveFilters ? "primary" : "default"}
              >
                Filter{" "}
                {hasActiveFilters
                  ? `(${
                      Object.keys(filters).filter(
                        (key) =>
                          filters[key] !== undefined &&
                          filters[key] !== null &&
                          filters[key] !== "" &&
                          !(
                            Array.isArray(filters[key]) &&
                            filters[key].length === 0
                          )
                      ).length
                    })`
                  : ""}
              </Button>
            </Badge>
            <Button onClick={handleRefresh} loading={loading}>
              Refresh
            </Button>
          </Space>
        }
      >
        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={isSmallScreen ? { x: 1200 } : undefined}
          />
        </div>
        {hasMore && !loading && (
          <div
            ref={observerTarget}
            style={{ height: "20px", margin: "20px 0" }}
          />
        )}
      </Card>

      {/* Filter Drawer */}
      <Drawer
        title="Filter Personal Customers"
        placement="right"
        onClose={() => setFilterVisible(false)}
        open={filterVisible}
        width={400}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFilterSubmit}
          initialValues={filters}
        >
          <Form.Item name="id" label="User ID">
            <Input
              placeholder="Search by ID"
              prefix={<IdcardOutlined style={{ color: "#1890ff" }} />}
              allowClear
            />
          </Form.Item>

          <Form.Item name="email" label="Email">
            <Input
              placeholder="Search by email"
              prefix={<MailOutlined style={{ color: "#1890ff" }} />}
              allowClear
            />
          </Form.Item>

          <Form.Item label="Status" name="status">
            <Select
              placeholder="Select status"
              allowClear
              style={{ width: "100%" }}
            >
              <Option value="new">New</Option>
              <Option value="pending-review">Pending Review</Option>
              <Option value="approved">Approved</Option>
              <Option value="declined">Declined</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Type" name="type">
            <Select
              placeholder="Select type"
              allowClear
              style={{ width: "100%" }}
            >
              <Option value="basic">Basic</Option>
              <Option value="premium">Premium</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={handleResetFilters}>Clear Selections</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<FilterOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      {/* User Profile Drawer */}
      <UserProfileDrawer
        visible={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        userType="individual"
        currentUser={currentUser}
        onStatusChange={handleStatusChange}
      />

      {/* Compliance Documents Drawer */}
      <ComplianceDrawer
        visible={complianceDrawerVisible}
        onClose={() => {
          setComplianceDrawerVisible(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        userType="individual"
        currentUser={currentUser}
      />

      {/* Transactions Modal */}
      <UserTransactionsModal
        visible={transactionsModalVisible}
        onClose={() => {
          setTransactionsModalVisible(false);
          setSelectedUserId(null);
          setSelectedUserName("");
        }}
        userId={selectedUserId}
        userName={selectedUserName}
      />

      {/* Add Cards Modal - Commented out since Cards column is hidden */}
      {/* <Modal
        title="User Cards"
        open={false}
        // open={cardsModalVisible}
        onCancel={() => {
          // setCardsModalVisible(false);
          // setSelectedUserCards([]);
        }}
        footer={null}
        width={800}
      >
        <Table
          dataSource={[]}
          // dataSource={selectedUserCards}
                      loading={false}
            // loading={loadingCards}
          columns={[
            {
              title: "Card ID",
              dataIndex: "id",
              key: "id",
            },
            {
              title: "Type",
              dataIndex: "card_type",
              key: "card_type",
              render: (type) => (
                <Tag color={type === "virtual" ? "blue" : "green" }}>
                  {type?.toUpperCase()}
                </Tag>
              ),
            },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              render: (status) => (
                <Tag
                  color={
                    status === "issued"
                      ? "success"
                      : status === "pending-collection"
                      ? "warning"
                      : status === "dispatched"
                      ? "processing"
                      : "default"
                  }
                >
                  {status?.toUpperCase()}
                </Tag>
              ),
            },
            {
              title: "Brand",
              dataIndex: "brand",
              key: "brand",
            },
            {
              title: "Last Four",
              dataIndex: "last_four",
              key: "last_four",
            },
            {
              title: "Created",
              dataIndex: "created",
              key: "created",
              render: (created) => formatDate(created),
            },
          ]}
          rowKey="id"
        />
      </Modal> */}
    </>
  );
};

IndividualUsers.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.string,
    role: PropTypes.string,
    email: PropTypes.string,
  }),
};

IndividualUsers.defaultProps = {
  currentUser: null,
};

export default IndividualUsers;
